import React, { createContext, useCallback, useContext, useMemo } from 'react';

import { scrollAreaToTop, scrollToFirstInvalidField } from 'shared/lib/scroll';

type ScrollAreaRef = React.RefObject<HTMLElement>;

interface ScrollAreaValue {
  /** Scroll the area back to the very top. */
  scrollToTop: (behavior?: ScrollBehavior) => void;
  /**
   * Bring the first field rendering a validation error into view and focus it.
   * Returns `true` when such a field was found.
   */
  scrollToFirstError: (behavior?: ScrollBehavior) => boolean;
}

const ScrollAreaContext = createContext<ScrollAreaRef | null>(null);

/**
 * Registers the element that actually scrolls for the subtree beneath it.
 * Layouts that scroll an inner element (instead of the document) provide their
 * container here so shared components can scroll the right thing.
 */
export const ScrollAreaProvider: React.FC<{
  containerRef: ScrollAreaRef;
  children: React.ReactNode;
}> = ({ containerRef, children }) => (
  <ScrollAreaContext.Provider value={containerRef}>
    {children}
  </ScrollAreaContext.Provider>
);

/**
 * Scroll helpers bound to the nearest registered scroll area. Without a
 * provider they operate on the window, so components work in both layouts.
 */
export const useScrollArea = (): ScrollAreaValue => {
  const containerRef = useContext(ScrollAreaContext);

  const scrollToTop = useCallback(
    (behavior: ScrollBehavior = 'smooth') => {
      scrollAreaToTop(containerRef?.current ?? null, behavior);
    },
    [containerRef],
  );

  const scrollToFirstError = useCallback(
    (behavior: ScrollBehavior = 'smooth') =>
      scrollToFirstInvalidField(containerRef?.current ?? null, behavior),
    [containerRef],
  );

  return useMemo(
    () => ({ scrollToTop, scrollToFirstError }),
    [scrollToTop, scrollToFirstError],
  );
};
